/**
 * Which view a hash names.
 *
 * A pure function over the hash string, in its own module, so it can be tested
 * without a browser. It used to live inside `app.tsx` and read
 * `window.location` itself, and it split the hash on `/` alone: the site screen
 * links to `#/audit?siteId=<uuid>`, so `head` was the whole string
 * `audit?siteId=…`, matched nothing, and the router fell through to the search
 * screen. The only link to a site's history in the product landed an operator
 * on the Operations overview with no error (LGN-125, audit H2).
 *
 * The query is therefore stripped before the path is split, and the one
 * parameter any view takes is lifted into the view itself. A view carrying its
 * own parameters is what lets `App` remount the screen when they change, so
 * moving between two sites' audit views re-filters instead of showing the first
 * site's rows under the second site's link.
 */

export type View =
  | { name: "search" }
  | { name: "site"; id: string }
  | { name: "owner"; id: string }
  | { name: "orgs" }
  | { name: "org"; id: string }
  | { name: "audit"; siteId: string; actor: string }
  | { name: "denylist" }
  | { name: "screening" }
  | { name: "flags" };

export function parseHash(hash: string): View {
  const withoutMarker = hash.replace(/^#\/?/, "");
  const [path, query] = splitOnce(withoutMarker, "?");
  const [head, tail] = path.split("/");
  const params = new URLSearchParams(query);

  if (head === "site" && tail) return { name: "site", id: tail };
  if (head === "owner" && tail) return { name: "owner", id: tail };
  if (head === "org" && tail) return { name: "org", id: tail };
  if (head === "orgs") return { name: "orgs" };
  if (head === "audit") {
    return {
      name: "audit",
      siteId: params.get("siteId") ?? "",
      actor: params.get("actor") ?? "",
    };
  }
  if (head === "denylist") return { name: "denylist" };
  if (head === "screening") return { name: "screening" };
  if (head === "flags") return { name: "flags" };
  return { name: "search" };
}

/**
 * Split on the FIRST separator only.
 *
 * `String.split` would cut a second `?` as well, and a value that contains one
 * is the caller's to decode, not this function's to lose.
 */
function splitOnce(value: string, separator: string): [string, string] {
  const at = value.indexOf(separator);
  return at === -1 ? [value, ""] : [value.slice(0, at), value.slice(at + separator.length)];
}
